import { styled } from "@mui/material";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import BrandclubVideo from "@/components/BrandclubVideo";
import { motion } from "motion/react";

const globalStyleOverrides = {
  margin: "0",
  padding: "0",
  fontSize: "100%",
  verticalAlign: "baseline",
  outline: "none",
  boxSizing: "border-box",
  lineHeight: "normal",
} as const;

export const H1 = styled("h1")({ ...globalStyleOverrides });
export const H2 = styled("h2")({ ...globalStyleOverrides });
export const H3 = styled("h3")({ ...globalStyleOverrides });
export const A = styled("a")({ ...globalStyleOverrides });
export const P = styled("p")({ ...globalStyleOverrides });
export const UL = styled("ul")({ ...globalStyleOverrides });
export const LI = styled("li")({ ...globalStyleOverrides });
export const Span = styled("span")({ ...globalStyleOverrides });
export const Img = styled(motion.img)({ ...globalStyleOverrides } as any);

export const Section = styled("div")(({ theme }) => ({
  position: "relative",
  scrollSnapAlign: "start",
  display: "flex",
  justifyContent: "center",
  "&:nth-of-type(1)": {
    padding: "0 0",
  },
  "&:nth-of-type(4)": {
    paddingBottom: "0",
  },
  "&.grey_bg": {
    background: "#f6f8fa",
  },
  margin: "auto",
}));

export const ContentContainer = styled("div")(({ theme }) => ({
  maxWidth: "1978px",
  width: "100%",
  padding: "0 5%",
}));

export const Content = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  position: "relative",
  paddingRight: "0",
  paddingLeft: "0",
  flex: "0 0 65.714286%",
  maxWidth: "1243px",
  width: "100%",
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 85.714286%",
    maxWidth: "85.714286%",
  },
}));

export const HeadContent = styled(Content)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

export const HeadBot = styled("div")(({ theme }) => ({
  flex: "1",
  display: "flex",
  justifyContent: "space-between",
  columnGap: "58px",
  paddingTop: "100px",
  "@media only screen and (max-width: 1280px)": {
    columnGap: 80,
    paddingBottom: "30px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: "80px",
  },
}));

export const ImageContainer = styled("div")(({ theme }) => ({
  height: "auto",
  position: "relative",
  display: "flex",
  alignItems: "center",
  "@media only screen and (max-width: 1280px)": {
    marginTop: "50px",
    marginBottom: "10px",
    height: "unset",
    justifyContent: "center",
  },
  [theme.breakpoints.down("md")]: {
    marginTop: "50px",
    marginBottom: "10px",
    height: "unset",
    justifyContent: "center",
  },
}));

export const HeaderVideosGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: 25,
  gridTemplateColumns: "repeat(2, auto)",
  gridTemplateRows: "repeat(2, auto)",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    maxWidth: "381px",
  },
}));

export const HeaderBrandclubVideo = styled(BrandclubVideo, {
  shouldForwardProp: (prop) => prop !== "main",
})<{ main?: boolean }>(({ theme, main }) => {
  const preview = !main;
  return {
    aspectRatio: main ? "370/555" : "1/1",
    width: main ? "370px" : "265px",
    borderRadius: "20px",
    "@media only screen and (max-width: 1280px)": {
      width: main ? "280px" : "201px",
    },
    cursor: preview ? "pointer" : undefined,
    gridRow: main ? "1 / span 2" : undefined,
    gridColumn: main ? "2" : undefined,
    [theme.breakpoints.down("md")]: {
      width: "100%",
      // prevents scrolling on mobile whenever the video is transitioning from preview to main
      minHeight: main ? "433px" : undefined,
      marginTop: "4%",
      flex: "1",
      gridRow: main ? "1" : undefined,
      gridColumn: main ? "1/span 2" : undefined,
    },
  };
});

export const HeaderVideoMainContainer = styled("div")(({ theme }) => ({
  // Add any specific styles for the main container if needed
}));

export const HeaderTextContent = styled(motion.div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
    alignItems: "center",
  },
}));

export const Header = styled("h1")(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "64px",
  lineHeight: "1.13",
  marginTop: "20px",
  marginBottom: "20px",
  color: theme.vars.palette.text.primary,
  "@media only screen and (max-width: 1280px)": {
    fontSize: "60px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "40px",
  },
}));

export const HeaderDescription = styled(P)(({ theme }) => ({
  color: theme.vars.palette.text.primary,
  fontSize: "20px",
  maxWidth: "430px",
  fontWeight: 500,
  lineHeight: "1.67",
  "@media only screen and (max-width: 1280px)": {
    maxWidth: "470px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    maxWidth: "100%",
  },
}));

export const TeachContent = styled(Content)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  marginTop: "200px",
  marginBottom: "125px",
  "@media only screen and (max-width: 1280px)": {
    marginTop: "100px",
    marginBottom: "105px",
  },
  [theme.breakpoints.down("sm")]: {
    marginTop: "60px",
    marginBottom: "0px",
  },

  ".teach_top": {
    fontSize: "42px",
    fontWeight: "bold",
    color: theme.vars.palette.text.primary,
    marginBottom: "90px",
    [theme.breakpoints.down("md")]: {
      marginBottom: "70px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "30px",
    },
  },

  ".teach_bot": {
    display: "flex",
    justifyContent: "space-between",
    position: "relative",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
    [theme.breakpoints.down("sm")]: {},
  },

  ".tech_star_top": {
    position: "absolute",
    top: "-55px",
    left: 0,
    "@media only screen and (max-width: 1280px)": {
      top: "-35px",
      left: 0,
    },
    [theme.breakpoints.down("sm")]: {
      top: "-35px",
      left: 0,
    },
  },

  ".tech_star_bot": {
    position: "absolute",
    right: 0,
    bottom: "-85px",
    "@media only screen and (max-width: 1280px)": {
      right: 0,
      bottom: "-35px",
    },
    [theme.breakpoints.down("sm")]: {
      right: 0,
      bottom: "-35px",
    },
  },

  ".tile": {
    zIndex: 1,
    maxWidth: "30%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "@media only screen and (max-width: 1280px)": {
      width: "100%",
      maxWidth: "100%",
      marginTop: "30px",
      marginBottom: "100px",
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: "130px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      marginTop: "30px",
      marginBottom: "100px",
    },
  },

  ".tile .img": {
    overflow: "hidden",
    width: "290px",
    height: "290px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },

  ".tile .img_bg": {
    width: "250px",
    height: "250px",
    maxWidth: "100%",
    position: "absolute",
    backgroundColor: "grey",
  },

  ".tile .tophalf": {
    width: "290px",
    height: "145px",
    position: "relative",
    overflow: "hidden",
  },

  ".tile .bothalf": {
    width: "290px",
    height: "145px",
    position: "relative",
    overflow: "hidden",
  },

  ".tile .tophalf:after": {
    content: '""',
    position: "absolute",
    borderRadius: "100%",
    top: "20px",
    left: "20px",
    width: "250px",
    height: "250px",
    boxShadow: "0px 0px 0px 250px #fff",
  },

  ".tile .bothalf:after": {
    content: '""',
    position: "absolute",
    top: "-125px",
    left: "20px",
    width: "250px",
    height: "250px",
    boxShadow: "0px 0px 0px 250px #fff",
    borderRadius: "100%",
  },

  ".tile .download_img": {
    height: "100%",
    position: "absolute",
  },

  ".tile .cash_img": {
    width: "100%",
    position: "absolute",
  },

  ".tile .retailer_img1, .tile .retailer_img2, .tile .retailer_img3": {
    height: "100%",
    position: "absolute",
  },

  ".tile .header": {
    fontSize: "24px",
    marginTop: "35px",
    fontWeight: "bold",
    lineHeight: "1.75",
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    "@media only screen and (max-width: 1280px)": {
      fontSize: "22px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "26px",
    },
  },

  ".tile .desc": {
    textAlign: "center",
    fontSize: "20px",
    maxWidth: "358px",
    width: "100%",
    lineHeight: "1.5",
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
    "@media only screen and (max-width: 1280px)": {
      maxWidth: "280px",
      fontSize: "16px",
      lineHeight: "1.63",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      fontSize: "16px",
    },
  },

  ".tile2 .desc": {
    "@media only screen and (max-width: 1280px)": {
      maxWidth: "290px",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      fontSize: "16px",
    },
  },

  ".tile3 .desc": {
    maxWidth: "290px",
    "@media only screen and (max-width: 1280px)": {
      maxWidth: "270px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
}));
export const DetailContent = styled(Content)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  padding: "125px 0 125px 10px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "100px 0",
  },

  ".detail_left": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    minWidth: "443px",
    "@media only screen and (max-width: 1280px)": {
      marginTop: "40px",
    },
    [theme.breakpoints.down("md")]: {
      minWidth: "unset",
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: "40px",
    },

    ".header": {
      width: "100%",
      maxWidth: "362px",
      fontSize: "42px",
      fontWeight: "bold",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "1.29",
      letterSpacing: "normal",
      textAlign: "left",
      marginBottom: "12px",
      color: theme.vars.palette.text.primary,
      "@media only screen and (max-width: 1280px)": {
        maxWidth: "455px",
      },
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%",
        fontSize: "26px",
      },
    },

    ".desc": {
      width: "100%",
      maxWidth: "450px",
      fontSize: "20px",
      fontWeight: 500,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "1.6",
      letterSpacing: "normal",
      textAlign: "left",
      color: theme.vars.palette.text.primary,
      "@media only screen and (max-width: 1280px)": {
        maxWidth: "505px",
      },
      [theme.breakpoints.down("md")]: {
        maxWidth: "451px",
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
      },
    },

    ".bullet_header": {
      fontSize: "16px",
      fontWeight: "bold",
      fontStretch: "normal",
      fontStyle: "normal",
      marginTop: "50px",
      letterSpacing: "normal",
      textAlign: "left",
    },

    ".bullet_point": {
      fontSize: "16px",
      fontWeight: 500,
      fontStretch: "normal",
      fontStyle: "normal",
      letterSpacing: "normal",
      textAlign: "left",
    },

    ul: {
      paddingLeft: "22px",
      li: {
        marginTop: "20px",
      },
    },
  },

  ".detail_right": {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
    },

    ".phone_img_top": {
      width: "auto",
      height: "625.5px",
      position: "absolute",
      [theme.breakpoints.down("md")]: {
        maxWidth: "452px",
        width: "100%",
        height: "auto",
      },
    },

    ".phone_img_bot": {
      width: "auto",
      height: "625.5px",
      [theme.breakpoints.down("md")]: {
        maxWidth: "452px",
        width: "100%",
        height: "auto",
      },
    },
  },

  "&.detailsec3": {
    paddingBottom: "200px",
    ".header": {
      maxWidth: "385px",
    },
    ".detail_right": {
      ".phone_img_top": {
        height: "602px",
        [theme.breakpoints.down("md")]: {
          height: "auto",
        },
      },
      ".phone_img_bot": {
        height: "602px",
        [theme.breakpoints.down("md")]: {
          height: "auto",
        },
      },
    },
    ".detail_left": {
      ".desc": {
        width: "430px",
        "@media only screen and (max-width: 1280px)": {
          width: "unset",
        },
      },
    },
  },
}));
export const Detail2Content = styled(Content)(({ theme }) => ({
  padding: "75px 0 75px 10px",
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  "@media only screen and (max-width: 1280px)": {
    padding: "125px 0",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "0",
  },

  ".detail2_right": {
    boxSizing: "border-box",
    display: "flex",
    minWidth: "413px",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      marginTop: "40px",
      minWidth: "unset",
    },

    ".header": {
      width: "100%",
      maxWidth: "445px",
      fontSize: "42px",
      fontWeight: "bold",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "1.29",
      letterSpacing: "normal",
      textAlign: "left",
      color: theme.vars.palette.text.primary,
      marginBottom: "12px",
      "@media only screen and (max-width: 1280px)": {
        maxWidth: "492px",
      },
      [theme.breakpoints.down("md")]: {
        marginRight: 0,
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "26px",
      },
    },

    ".desc": {
      fontSize: "20px",
      width: "100%",
      maxWidth: "396px",
      fontWeight: 500,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "1.6",
      letterSpacing: "normal",
      textAlign: "left",
      color: theme.vars.palette.text.primary,
      "@media only screen and (max-width: 1280px)": {
        maxWidth: "505px",
        fontSize: "20px",
      },
      [theme.breakpoints.down("md")]: {
        maxWidth: "451px",
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
      },
    },
  },

  ".detail2_left": {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      justifyContent: "center",
    },
    [theme.breakpoints.down("sm")]: {},

    ".phone_img_top": {
      width: "auto",
      height: "626px",
      position: "relative",
      [theme.breakpoints.down("md")]: {
        maxWidth: "452px",
        width: "100%",
        height: "auto",
      },
    },

    ".phone_img_bot": {
      width: "100%",
      height: "626px",
      position: "absolute",
      [theme.breakpoints.down("md")]: {
        maxWidth: "452px",
        width: "100%",
        height: "auto",
      },
    },
  },
}));
export const VideosContent = styled(Content)(({ theme }) => ({
  color: theme.vars.palette.text.primary,
  padding: "80px 40px",
  "@media only screen and (max-width: 1280px)": {
    padding: "133px 40px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "90px 0 140px 0",
  },

  ".videos_container": {
    position: "relative",
  },

  ".header_container": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "40px",
    },
  },

  ".videos_title": {
    fontSize: "42px",
    fontWeight: "bold",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "30px",
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: "20px",
    },
  },

  ".videos_subtitle": {
    fontSize: "20px",
    maxWidth: "560px",
    margin: "24px auto",
    fontWeight: 500,
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },

  ".videos_desc": {
    margin: "0 auto",
    fontSize: "24px",
    fontWeight: 500,
    textAlign: "center",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },

    ".videos_text": {
      maxWidth: "544px",
    },

    ".comment": {
      fontSize: "20px",
      fontWeight: 600,
    },

    ".name": {
      marginTop: "12px",
      fontWeight: "bold",
      fontSize: "16px",
    },

    ".city": {
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "16px",
      marginTop: "12px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
      "> span": {
        padding: "0 5px",
      },
    },
  },

  ".people": {
    margin: "50px auto",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },

    ".card": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      margin: "auto",
      width: "344px",
      height: "430px",
      transition: "all 0.5s ease",
      transform: "scale(0.65)",
      padding: "0 10px",
      [theme.breakpoints.down("sm")]: {
        margin: "0 20px",
        width: "285px",
        height: "350px",
        transform: "scale(0.6)",
      },

      "&.select": {
        transform: "scale(1)",
        marginTop: "0",
      },

      ".brandclub_video": {
        margin: "auto",
        borderRadius: "15px",
        [theme.breakpoints.down("sm")]: {
          margin: "auto",
        },
      },
    },
  },

  ".people_mobile": {
    maxWidth: 452,
    margin: "0 auto",
    ".brandclub_video": {
      borderRadius: "15px",
    },
  },

  ".videos_button": {
    border: "none",
    outline: "none",
    width: "40px",
    height: "40px",
    borderRadius: "100px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    cursor: "pointer",
    padding: "0",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },

    "&.maxleft": {
      bottom: "50%",
      left: "-40px",
      "@media only screen and (max-width: 1280px)": {
        left: "-30px",
      },
    },

    "&.maxright": {
      bottom: "50%",
      right: "-40px",
      "@media only screen and (max-width: 1280px)": {
        right: "-30px",
      },
    },

    img: {
      width: "100%",
      height: "100%",
    },
  },

  ".mobile_video_slider": {
    [theme.breakpoints.down("md")]: {
      "> div": {
        overflow: "visible",
      },
    },
  },
}));
export const ShophomeContent = styled(Content)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  paddingTop: "200px",
  paddingLeft: "40px",
  minWidth: "100%",
  paddingBottom: "40px",
  alignItems: "flex-start",
  columnGap: "60px",
  "@media only screen and (max-width: 1280px)": {
    paddingTop: "200px",
    paddingBottom: "65px",
    flexDirection: "column",
    alignItems: "center",
  },
  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
    paddingTop: "180px",
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: "100px",
    paddingBottom: "20px",
    flexDirection: "column",
  },

  ".shop_left": {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "617.8px",
    [theme.breakpoints.down("md")]: {
      width: "600px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },

    ".shop_img_bot": {
      width: "100%",
      position: "relative",
      display: "flex",
    },

    ".shop_img_top": {
      width: "100%",
      position: "absolute",
      display: "flex",
      top: "2.90%",
      maxWidth: "57.5%",
    },
  },

  ".shop_right": {
    "@media only screen and (max-width: 1280px)": {
      marginTop: "-144px",
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: "-15%",
    },
    ".shop_top": {
      paddingTop: "15px",
      display: "flex",
      flexDirection: "column",
      width: "609px",
      "@media only screen and (max-width: 1280px)": {
        paddingLeft: "0px",
        paddingTop: "7px",
        display: "flex",
        alignItems: "center",
        width: "705px",
      },
      [theme.breakpoints.down("md")]: {
        margin: "0 auto",
        textAlign: "center",
        width: "100%",
        maxWidth: "510px",
      },
      [theme.breakpoints.down("sm")]: {
        alignItems: "flex-start",
      },

      ".header": {
        fontSize: "42px",
        fontWeight: "bold",
        fontStretch: "normal",
        fontStyle: "normal",
        textAlign: "left",
        lineHeight: "1.29",
        letterSpacing: "normal",
        color: theme.vars.palette.text.primary,
        marginBottom: "15px",
        "@media only screen and (max-width: 1280px)": {
          marginBottom: "10px",
          textAlign: "center",
        },
        [theme.breakpoints.down("md")]: {
          fontSize: "36px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "26px",
          textAlign: "left",
        },
      },

      ".desc": {
        fontSize: "20px",
        fontWeight: 500,
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "1.6",
        letterSpacing: "normal",
        textAlign: "left",
        color: theme.vars.palette.text.primary,
        width: "100%",
        maxWidth: "502px",
        "@media only screen and (max-width: 1280px)": {
          textAlign: "center",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "16px",
          maxWidth: "450px",
          textAlign: "left",
        },
      },
    },

    ".shop_bot": {
      display: "grid",
      gridTemplateColumns: "repeat(2, auto)",
      columnGap: "30px",
      "@media only screen and (max-width: 1280px)": {
        paddingLeft: "0px",
        marginTop: "70px",
        justifyContent: "center",
      },
      [theme.breakpoints.down("md")]: {
        marginTop: "45px",
        // columnGap: "20px",
      },
      [theme.breakpoints.down("sm")]: {
        paddingLeft: "0px",
        marginTop: "30px",
        gridTemplateColumns: "1fr",
      },

      ".shop_card": {
        paddingTop: "35px",
        paddingBottom: "10px",
        [theme.breakpoints.down("md")]: {
          paddingBottom: "35px",
        },
        [theme.breakpoints.down("sm")]: {
          paddingTop: "0px",
          paddingBottom: "30px",
        },

        ".desc": {
          fontSize: "16px",
          width: "260px",
          fontWeight: 500,
          fontStretch: "normal",
          fontStyle: "normal",
          lineHeight: "1.5",
          letterSpacing: "normal",
          textAlign: "left",
          paddingLeft: "30px",
          color: theme.vars.palette.text.primary,
          [theme.breakpoints.down("sm")]: {
            fontSize: "12px",
            maxWidth: "260px",
            width: "100%",
          },
        },
      },

      ".title_container": {
        display: "flex",
        alignItems: "center",
        marginBottom: "7.6px",

        ".icon": {
          display: "flex",
          alignItems: "center",
          width: "18px",
          height: "18px",
          justifyContent: "center",
          marginRight: "16px",
          marginTop: "-7px",
          [theme.breakpoints.down("sm")]: {
            display: "flex",
            alignItems: "center",
            width: "18px",
            height: "18px",
            justifyContent: "center",
            marginRight: "10px",
            marginTop: "0px",
          },

          img: {
            width: "unset",
            height: "unset",
            maxWidth: "100%",
            maxHeight: "100%",
            [theme.breakpoints.down("sm")]: {
              width: "18px",
              height: "18px",
            },
          },
        },

        ".title": {
          fontSize: "20px",
          fontWeight: "bold",
          fontStretch: "normal",
          fontStyle: "normal",
          letterSpacing: "normal",
          textAlign: "left",
          color: theme.vars.palette.text.primary,
          [theme.breakpoints.down("sm")]: {
            fontSize: "19px",
          },
        },
      },
    },
  },
}));
export const EnrichContent = styled(Content)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  paddingBottom: "150px",
  "@media only screen and (max-width: 1280px)": {
    paddingTop: "100px",
  },
  [theme.breakpoints.down("md")]: {
    paddingBottom: "100px",
  },

  ".enrich_top": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    ".header": {
      fontSize: "42px",
      fontWeight: "bold",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "1.29",
      letterSpacing: "normal",
      textAlign: "center",
      color: theme.vars.palette.text.primary,
      marginBottom: "10px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "26px",
      },
    },

    ".desc": {
      width: "100%",
      maxWidth: "666px",
      fontSize: "20px",
      fontWeight: 500,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "1.67",
      letterSpacing: "normal",
      textAlign: "center",
      color: theme.vars.palette.text.primary,
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
        maxWidth: "100%",
      },
    },
  },

  ".enrich_bot": {
    display: "flex",
    justifyContent: "center",
    position: "relative",
    width: "100%",
    padding: "0 120px",
    marginTop: "60px",
    columnGap: " 90px",
    "@media only screen and (max-width: 1280px)": {
      alignItems: "center",
      padding: "0px",
    },
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      marginTop: "100px",
    },
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      alignItems: "center",
    },

    ".enrich_left": {
      position: "relative",
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("md")]: {
        marginBottom: "130px",
      },

      ".enrich_phone_top": {
        width: "481px",
        position: "absolute",
        "@media only screen and (max-width: 1280px)": {
          width: "420px",
        },
        [theme.breakpoints.down("sm")]: {
          width: "100%",
          maxWidth: "387px",
        },
      },

      ".enrich_phone_bot": {
        width: "481px",
        "@media only screen and (max-width: 1280px)": {
          width: "420px",
        },
        [theme.breakpoints.down("sm")]: {
          width: "100%",
          maxWidth: "387px",
        },
      },
    },

    ".enrich_right": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      marginTop: "54px",

      [theme.breakpoints.down("md")]: {
        justifyContent: "center",
        marginTop: "0px",
      },

      ".enrich_card": {
        zIndex: 1,
        alignItems: "flex-start",
        display: "flex",
        [theme.breakpoints.down("sm")]: {},

        ".tag": {
          width: "35px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "1px",
          "@media only screen and (max-width: 1280px)": {
            marginTop: "0px",
          },
        },

        img: {
          marginTop: "-2px",
        },

        ".desc": {
          width: "100%",
          paddingLeft: "12px",
          maxWidth: "420px",
          lineHeight: "1.63",
          fontSize: "20px",
          fontWeight: 500,
          color: theme.vars.palette.text.primary,
          "@media only screen and (max-width: 1280px)": {
            maxWidth: "400px",
          },
          [theme.breakpoints.down("sm")]: {
            fontSize: "16px",
          },
        },
      },
    },
  },
}));
export const ShareContent = styled(Content)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  columnGap: "190px",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: "35px",
  paddingBottom: "150px",
  "@media only screen and (max-width: 1280px)": {
    paddingTop: "200px",
    paddingBottom: "150px",
    columnGap: "109px",
  },
  [theme.breakpoints.down("md")]: {
    paddingTop: "80px",
    paddingBottom: "100px",
    flexDirection: "column",
  },

  ".share_left": {
    position: "relative",
    width: "100%",
    maxWidth: "615px",
    "@media only screen and (max-width: 1280px)": {
      maxWidth: "527px",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "474px",
    },
    display: "flex",
    alignItems: "center",
  },

  ".share_left img": {
    width: "100%",
    "@media only screen and (max-width: 1280px)": {
      // width: "unset",
    },
  },

  ".share_sec_top": {
    width: "100%",
    position: "absolute",
  },

  ".share_sec_bot": {
    width: "526px",
    "@media only screen and (max-width: 1280px)": {
      width: "100%",
    },
  },

  ".share_right": {
    maxWidth: "431px",
    "@media only screen and (max-width: 1280px)": {
      marginTop: "100px",
      maxWidth: "100%",
    },
    [theme.breakpoints.down("md")]: {
      marginTop: "0px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: "0px",
      marginBottom: "40px",
    },
  },

  ".share_right .header": {
    fontSize: "42px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.29",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.vars.palette.text.primary,
    marginBottom: "10px",
    "@media only screen and (max-width: 1280px)": {
      width: "100%",
      maxWidth: "408px",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "474px",
      textAlign: "center",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "26px",
      width: "100%",
      margin: "0",
      marginBottom: "10px",
      textAlign: "left",
    },
  },

  ".share_right .desc": {
    fontSize: "20px",
    fontWeight: 500,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.67",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.vars.palette.text.primary,
    "@media only screen and (max-width: 1280px)": {
      maxWidth: "356px",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "424px",
      textAlign: "center",
      marginBottom: "40px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      textAlign: "left",
    },
  },
  ".share_right .desc button": {
    [theme.breakpoints.down("md")]: {
      marginLeft: "auto",
      marginRight: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0",
      marginRight: "0",
    },
  },
}));
export const LockContent = styled(Content)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: "50px",
  paddingBottom: "50px",

  ".lock_sec_header_icon": {
    width: "127px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "80px",
    },
  },

  ".header": {
    maxWidth: "662px",
    marginTop: "30px",
    marginBottom: "12px",
    fontSize: "42px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.29",
    letterSpacing: "normal",
    textAlign: "center",
    color: theme.vars.palette.text.primary,

    [theme.breakpoints.down("md")]: {
      maxWidth: 470,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "26px",
    },
  },

  ".desc": {
    maxWidth: "652px",
    fontSize: "20px",
    fontWeight: 500,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.6",
    letterSpacing: "normal",
    textAlign: "center",
    color: theme.vars.palette.text.primary,
    "@media only screen and (max-width: 1280px)": {
      fontSize: "20px",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: 480,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
}));

const Site = styled("div")(() => ({
  overflowX: "hidden",
  overflowY: "auto",
  height: "100vh",
  boxSizing: "border-box",
}));

export default Site;
